/**
 * /admin/worksheets — Admin worksheet enable/disable toggle (Fase 4 N-11).
 *
 * Reads from /api/admin/worksheets (worksheet_catalog + worksheet_state).
 * Filter chips group by status (ready / in_progress). Search by judul.
 */

import type { ReactNode } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { freshSessionQuery } from '../../lib/api/queries.js';
import {
  CatalogTogglePanel,
  type CatalogItemBase,
  type CatalogColumn,
  type FilterOption,
} from '../../components/admin/CatalogTogglePanel.js';

interface WorksheetRow extends CatalogItemBase {
  /** Backend identifier on the wire — the catalog API returns
   *  `worksheetId` rather than the generic `id`. The route remaps it to
   *  `id` so the panel's mutation path resolves correctly. */
  worksheetId: string;
  bundleNo: number;
  bagian: number;
  level: string;
  konsep: string;
  /** Indonesian for "title" — the worksheet API returns `judul`, the
   *  route remaps to `title` for the panel. */
  judul: string;
  slug: string;
  thumbnailUrl: string | null;
}

const statusFilters: FilterOption[] = [
  {
    label: 'Siap',
    value: 'ready',
    matches: (item, v) => item.status === v,
  },
  {
    label: 'Dalam proses',
    value: 'in_progress',
    matches: (item, v) => item.status === v,
  },
];

const columns: CatalogColumn<WorksheetRow>[] = [
  { label: 'Bagian', render: (w) => w.bagian },
  { label: 'Level', render: (w) => w.level },
  { label: 'Konsep', render: (w) => w.konsep },
];

export const Route = createFileRoute('/admin/worksheets')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'tutor' && user.isApprovedTutor) {
      throw redirect({ to: '/dashboard' });
    }
    if (user.role === 'tutor' || user.kind === 'student') {
      throw redirect({ to: '/me' });
    }
    if (user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
    return null;
  },
  component: AdminWorksheetsPage,
});

function AdminWorksheetsPage(): ReactNode {
  const fetchJson = async <T,>(input: RequestInfo, init?: RequestInit): Promise<T> => {
    const res = await fetch(input, {
      ...init,
      credentials: 'include',
      headers: { ...(init?.headers ?? {}), accept: 'application/json' },
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
      throw new Error(body.error?.message ?? `HTTP ${res.status}`);
    }
    return (await res.json()) as T;
  };

  // Wrap fetchJson to (1) convert the { worksheets: [...] } response shape
    // into the generic { items: [...] } shape CatalogTogglePanel expects, and
    // (2) normalize the per-row field names: backend returns `worksheetId` /
    // `judul` (Indonesian), but the panel's CatalogItemBase expects `id` /
    // `title`. Without this remap, the row title shows blank and the toggle
    // path becomes `/api/admin/worksheets/undefined/toggle`.
    const wrappedFetch = async <T,>(input: RequestInfo, init?: RequestInit): Promise<T> => {
      const res = await fetchJson<{
        ok: boolean;
        data: { worksheets: WorksheetRow[]; total: number };
      }>(input, init);
      return {
        ok: res.ok,
        data: {
          items: res.data.worksheets.map((w) => ({
            ...w,
            id: w.worksheetId ?? w.id,
            title: w.judul ?? w.title,
          })),
          total: res.data.total,
        },
      } as unknown as T;
    };

  return (
    <main className="page-shell" data-testid="admin-worksheets-page">
      <PageHeader
        eyebrow="Manajemen"
        title="Toggle worksheet"
        description="Aktifkan atau nonaktifkan worksheet di katalog. Worksheet yang dinonaktifkan tersembunyi dari siswa sampai diaktifkan kembali."
      />
      <CatalogTogglePanel<WorksheetRow>
        kind="worksheet"
        fetchJson={wrappedFetch}
        columns={columns}
        filterOptions={statusFilters}
        searchPlaceholder="Cari judul worksheet…"
      />
    </main>
  );
}
